import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FlexContainer } from '@/components/containers/FlexContainer/FlexContainer';
import Label from '@/components/form/Label/Label';
import { Button, Box, Typography, Select, MenuItem, Grid } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchFormData } from '@/redux/actions/searchFormDataActions';
import IOSPicker from '@/components/reusable/IOSPicker/IOSPicker';

const convertToComparableTime = time => {
  let hour = time.hour;
  if (time.period === 'PM' && hour !== 12) hour += 12;
  if (time.period === 'AM' && hour === 12) hour = 0;
  return hour;
};

const formatTimeDisplay = t => {
  const minute = String(t.minute ?? 0).padStart(2, '0');
  return `${t.hour}:${minute} ${t.period}`;
};

const toTimeObj = str => {
  const [time, period] = str.split(' ');
  const [hour, minute] = time.split(':');
  return { hour: parseInt(hour), minute: parseInt(minute), period };
};

const generateTimes = () => {
  const arr = [];
  for (let h = 7; h <= 23; h++) {
    const period = h >= 12 ? 'PM' : 'AM';
    let hour = h % 12;
    if (hour === 0) hour = 12;
    arr.push({ hour, minute: 0, period });
  }
  return arr;
};

const ReservationSection = ({
  languageData,
  isAuthenticated,
  openSignInModal,
  spaceData,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { customText, idSubSpace } = useParams();
  const currentSearchFormData = useSelector(s => s.searchFormData);

  const [date, setDate] = useState(
    currentSearchFormData?.startDate || '2026-03-19',
  );

  const [startTime, setStartTime] = useState({
    hour: 9,
    minute: 0,
    period: 'AM',
  });
  const [endTime, setEndTime] = useState({ hour: 5, minute: 0, period: 'PM' });

  const times = useMemo(() => generateTimes(), []);
  const startComparable = convertToComparableTime(startTime);
  const endComparable = convertToComparableTime(endTime);
  const isButtonActive =
    startComparable >= 7 &&
    startComparable <= 23 &&
    endComparable > startComparable;

  const handleStartChange = e => {
    const parsed = toTimeObj(e.target.value);
    setStartTime(parsed);
    const newStart = convertToComparableTime(parsed);
    const currentEnd = convertToComparableTime(endTime);
    if (currentEnd <= newStart) {
      const next = times.find(t => convertToComparableTime(t) > newStart);
      if (next) setEndTime(next);
    }
  };

  const handleEndChange = e => setEndTime(toTimeObj(e.target.value));

  const handleSubmit = () => {
    if (!isButtonActive) return;
    if (!isAuthenticated) {
      openSignInModal();
      return;
    }
    dispatch(
      setSearchFormData({
        ...currentSearchFormData,
        startDate: date,
        startTime: formatTimeDisplay(startTime),
        endTime: formatTimeDisplay(endTime),
      }),
    );
    const baseRoute =
      languageData === 'EN'
        ? `/space-detail/${customText}/${idSubSpace}`
        : `/detalle-espacio/${customText}/${idSubSpace}`;
    navigate(`${baseRoute}/true`);
  };

  return (
    <FlexContainer className="bgGradientBlue borderrad14 flex-column pad16">
      <Typography fontWeight={600} mb={2} fontSize={16} color="white">
        {languageData === 'EN' ? 'Your Reservation' : 'Tu reservación'}
      </Typography>

      {/* DATE + PEOPLE */}
      <Box bgcolor="white" p={1.5} borderRadius={1} mb={1.5}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Label className="fontBlack">
              {languageData === 'EN' ? 'Date' : 'Fecha'}
            </Label>
            <Select
              value={date}
              variant="standard"
              fullWidth
              size="small"
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#4e639b',
                    borderRadius: '12px',
                    mt: 1,
                    '& .MuiList-root': { padding: 0 },
                  },
                },
              }}
            >
              <Box
                className="ReservationDateViewDesktop"
                p={2}
                onKeyDown={e => e.stopPropagation()}
              >
                <IOSPicker selectedDate={date} setSelectedDate={setDate} />
              </Box>

              <MenuItem value={date} sx={{ display: 'none' }}>
                {date}
              </MenuItem>
            </Select>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Label className="fontBlack">
              {languageData === 'EN' ? 'People' : 'Personas'}
            </Label>

            <Box
              sx={{
                borderBottom: '1px solid rgba(0,0,0,0.42)',
                padding: '5px 0',
                fontSize: '14px',
                color: 'black',
              }}
            >
              {languageData === 'EN'
                ? `Up to ${spaceData?.host_capacity} people`
                : `Hasta ${spaceData?.host_capacity} personas`}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box bgcolor="white" p={1.5} borderRadius={1} mb={2}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Label className="fontBlack">
              {languageData === 'EN' ? 'Start Time' : 'Hora inicio'}
            </Label>
            <Select
              value={formatTimeDisplay(startTime)}
              onChange={handleStartChange}
              variant="standard"
              fullWidth
              size="small"
            >
              {times.map(t => (
                <MenuItem
                  key={formatTimeDisplay(t)}
                  value={formatTimeDisplay(t)}
                >
                  {formatTimeDisplay(t)}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Label className="fontBlack">
              {languageData === 'EN' ? 'End Time' : 'Hora fin'}
            </Label>
            <Select
              value={formatTimeDisplay(endTime)}
              onChange={handleEndChange}
              variant="standard"
              fullWidth
              size="small"
            >
              {times.map(t => (
                <MenuItem
                  key={formatTimeDisplay(t)}
                  value={formatTimeDisplay(t)}
                  disabled={convertToComparableTime(t) <= startComparable}
                >
                  {formatTimeDisplay(t)}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </Box>

      <Button
        fullWidth
        variant="contained"
        disabled={!isButtonActive}
        onClick={handleSubmit}
        sx={{
          background: isButtonActive
            ? 'linear-gradient(90deg,#ff6fa5,#7a7cff)'
            : 'rgba(255,255,255,0.3)',
          borderRadius: '5px',
          textTransform: 'none',
          fontWeight: 600,
          color: 'white',
        }}
      >
        {languageData === 'EN' ? 'Reserve Now' : 'Reservar ahora'}
      </Button>
    </FlexContainer>
  );
};

export default ReservationSection;
